import { AnyAction } from 'redux';
import { STATE } from 'app/constants';
import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import * as navigate from 'app/area/shared/navigation';
import { isForbiddenStatus, blockAccess, isNotFoundStatus } from 'app/area/shared/actions-utils';
import { mapDuplicateImportSetItemWire, mapImportSetFromWire, mapImportSetToWire } from './utils/map-import-set';
import { importSetAdminTranslation } from '../constants';
import {
  RequestLanguageOptions, ReceiveLanguageOptions, FailLanguageOptions,
  RequestImportSet, ReceiveImportSet, FailImportSet,
  RequestDeleteImportSet, ReceiveDeleteImportSet, FailDeleteImportSet,
  RequestSaveImportSet, ReceiveSaveImportSet, FailSaveImportSet,
  UpdateItem, ChangeLanguage, CopyLanguageToAll,
  ClearForm, UpdateValidation, ResetValidation
} from './action-types';
import { api } from './api';
import { getBackRoute } from './route';
import { ImportSetItem, ImportSetItemValidation } from './types-store';
import { ImportSetActionType } from 'app/area/import-sets-administration/form/types';
import { replaceTemplateString } from 'app/infrastructure/platform';

export const actionCreators = {

  loadLanguageOptions: (): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestLanguageOptions());

      try {

        const languageOptions = await api.loadLanguageOptions();

        dispatch(new ReceiveLanguageOptions(
          languageOptions.map(option => ({
            value: option.v,
            description: option.d
          }))
        ));
      } catch (err) {

        if (err && (err.userMessages || err.message)) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        dispatch(new FailLanguageOptions());
      }
    },

  createImportSet: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => dispatch(new ReceiveImportSet(mapImportSetFromWire({}))),

  loadImportSet: (id: number, actionType: ImportSetActionType, locale: string): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestImportSet());

      try {

        const importSet = await api.loadImportSet(id);
        const importSetItem = mapImportSetFromWire(importSet, actionType, locale);

        dispatch(new ReceiveImportSet(importSetItem));

      } catch (err) {

        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.importSetAdministration,
            translationId: importSetAdminTranslation.no_access_to_import_set_admin
          });
        } else {

          const errorMessage = err && (err.userMessages || err.message);
          !!errorMessage && dispatch(new ErrorNotification(errorMessage));

          navigate.to({ pathname: getBackRoute() });
        }

        dispatch(new FailImportSet());
      }
    },

  deleteImportSet: (id: number, importSetText: string, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      dispatch(new RequestDeleteImportSet());

      try {
        await api.deleteImportSet(id);

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });

        dispatch(new ReceiveDeleteImportSet());
        dispatch(new SuccessNotification(replaceTemplateString(successMessage, importSetText)));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDeleteImportSet());

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  saveImportSet: (
    actionType: ImportSetActionType,
    importSet: ImportSetItem,
    successMessage: string,
    validationErrorMessage: string
  ): ActionDispatching<AnyAction, ApplicationState> =>
    async (dispatch, getState) => {

      const id = importSet?.id;

      dispatch(new RequestSaveImportSet());

      try {
        const importSetWire = mapImportSetToWire(importSet);

        if (actionType === ImportSetActionType.edit) {
          await api.updateImportSet(id, importSetWire);
        } else if (actionType === ImportSetActionType.duplicate) {
          await api.duplicateImportSet(id, mapDuplicateImportSetItemWire(importSetWire));
        } else {
          await api.addImportSet(
            importSetWire, actionType === ImportSetActionType.codelikecode && { importCodeLikeCode: true }
          );
        }

        dispatch(new ReceiveSaveImportSet());

        navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        dispatch(new SuccessNotification(successMessage));

      } catch (err) {

        dispatch(new FailSaveImportSet());

        if (Object.keys(err?.validationErrors ?? {}).length > 0) {
          dispatch(new ErrorNotification(validationErrorMessage));
          dispatch(new UpdateValidation(complexValidationFromServer(err.validationErrors)));
        }

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }

        if (isNotFoundStatus(err)) {
          navigate.to({ pathname: getBackRoute() }, { state: { internal: true } });
        }
      }
    },

  clearForm: (): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ClearForm());
    },

  updateItem: (item: Partial<ImportSetItem>): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateItem(item));
    },

  changeLanguage: (languageId: number, text: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ChangeLanguage(languageId, text));
    },

  copyLanguageToAll: (languageId: number, successMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new CopyLanguageToAll(languageId));
      dispatch(new SuccessNotification(successMessage));
    },

  updateValidation: (validation: ImportSetItemValidation): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new UpdateValidation(validation));
    },

  resetValidation: (key: string): ActionDispatching<AnyAction, ApplicationState> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(key));
  },

  displayError: (errorMessage: string): ActionDispatching<AnyAction, ApplicationState> =>
    (dispatch, getState) => {
      dispatch(new ErrorNotification(errorMessage));
    }
};
